import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdClose, 
  MdArrowBack, 
  MdChevronRight,
  MdMoreVert,
  MdVisibility,
  MdVisibilityOff,
  MdFileDownload,
  MdAttachFile,
  MdDescription,
  MdFolder
} from 'react-icons/md';
import { CiMenuKebab } from "react-icons/ci";
import InputMask from 'react-input-mask';

const UserStatusBadge = ({ isActive }) => {
  const { t } = useLanguage();
  return (
    <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${
      isActive 
        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
    }`}>
      {isActive ? t('customerDetail.active') : t('customerDetail.inactive')}
    </span>
  );
};

const ActionButtons = ({ user, onEdit, onDelete }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onEdit(user)}
        className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        title={t('customerDetail.editUser')}
      >
        <MdEdit size={18} />
      </button>
      <button
        onClick={() => onDelete(user.id, user.full_name)}
        className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
        title={t('customerDetail.deleteUser')}
      >
        <MdDelete size={18} />
      </button>
    </div>
  );
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [customer, setCustomer] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCustomerEditForm, setShowCustomerEditForm] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [expandedContracts, setExpandedContracts] = useState({});
  const [contractFilesVisible, setContractFilesVisible] = useState({});
  const [formData, setFormData] = useState({
    user_status: true,
    full_name: '',
    username: '',
    password: '',
    confirmPassword: '',
    user_email: '',
    user_gsm: '',
    user_type: 'customer',
    user_department: '',
    customer_id: null
  });
  const [customerFormData, setCustomerFormData] = useState({
    cutomer_status: true,
    customer_name: '',
    customer_address: '',
    customer_phone: '',
    customer_contact: '',
    customer_contact_email: '',
    customer_contact_phone: '',
    customer_account_manager: '',
    company_id: null
  });
  const [agents, setAgents] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });

  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [showCustomerActionsMenu, setShowCustomerActionsMenu] = useState(false);
  const [showUsersActionsMenu, setShowUsersActionsMenu] = useState(false);

  // Products Table Component
  const ProductsTable = ({ contractId }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchProducts = async () => {
        try {
          setLoading(true);
          const response = await api.get(`/products/${contractId}`);
          if (response.data.success) {
            setProducts(response.data.products);
          }
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchProducts();
    }, [contractId]);

    if (loading) {
      return <div className="text-center py-4 text-gray-600 dark:text-gray-400">{t('customerDetail.loadingProducts')}</div>;
    }

    if (products.length === 0) {
      return <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">{t('customerDetail.noProductsFound')}</div>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-600 rounded-md">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('customerDetail.productName')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('customerDetail.slaStatus')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('customerDetail.slaDetails')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {products.map((product) => (
              <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-2">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {product.product_name}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    product.sla_group_id 
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {product.sla_group_id ? t('customerDetail.slaAssigned') : t('customerDetail.noSLA')}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {product.sla_group_id ? (
                    <div className="text-sm text-gray-900 dark:text-white">
                      <span className="font-medium">{product.sla_group_name}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('customerDetail.noSlaAssigned')}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Contract Files Table Component
  const ContractFilesTable = ({ contractId }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchFiles = async () => {
        try {
          setLoading(true);
          const response = await api.get(`/contracts/${contractId}/files`);
          if (response.data.success) {
            setFiles(response.data.files);
          }
        } catch (error) {
          console.error('Error fetching contract files:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchFiles();
    }, [contractId]);

    const handleDownloadFile = async (fileId, fileName) => {
      try {
        const response = await api.get(`/contracts/files/${fileId}/download`, {
          responseType: 'blob'
        });
        
        // Create a blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        
        // Append to html link element page
        document.body.appendChild(link);
        
        // Start download
        link.click();
        
        // Clean up and remove the link
        link.parentNode.removeChild(link);
      } catch (error) {
        console.error('Error downloading file:', error);
        alert(t('customerDetail.errorDownloadingFile'));
      }
    };

    // Get file icon based on file extension
    const getFileIcon = (filename) => {
      const extension = filename.split('.').pop().toLowerCase();
      
      switch(extension) {
        case 'pdf':
          return <span className="text-red-600 dark:text-red-400"><MdDescription size={20} /></span>;
        case 'doc':
        case 'docx':
          return <span className="text-blue-600 dark:text-blue-400"><MdDescription size={20} /></span>;
        case 'xls':
        case 'xlsx':
          return <span className="text-green-600 dark:text-green-400"><MdDescription size={20} /></span>;
        case 'ppt':
        case 'pptx':
          return <span className="text-orange-600 dark:text-orange-400"><MdDescription size={20} /></span>;
        default:
          return <span className="text-gray-600 dark:text-gray-400"><MdAttachFile size={20} /></span>;
      }
    };

    if (loading) {
      return <div className="text-center py-4 text-gray-600 dark:text-gray-400">{t('customerDetail.loadingFiles')}</div>;
    }

    if (files.length === 0) {
      return <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">{t('customerDetail.noFilesFound')}</div>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-600 rounded-md">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[5%]">
                {t('customerDetail.type')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[35%]">
                {t('customerDetail.fileName')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[35%]">
                {t('customerDetail.description')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[15%]">
                {t('customerDetail.uploadDate')}
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[10%]">
                {t('customerDetail.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {files.map((file) => (
              <tr key={file.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-2">
                  <div className="flex items-center justify-center">
                    {getFileIcon(file.filename)}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {file.filename}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {file.file_description}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(file.created_at).toLocaleString()}
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => handleDownloadFile(file.id, file.filename)}
                    className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                    title={t('customerDetail.downloadFile')}
                  >
                    <MdFileDownload size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Form temizleme fonksiyonu
  const resetFormData = () => {
    setFormData({
      user_status: true,
      full_name: '',
      username: '',
      password: '',
      confirmPassword: '',
      user_email: '',
      user_gsm: '',
      user_type: 'customer',
      user_department: '',
      customer_id: customer ? customer.id : null
    });
    setIsEditing(false);
    setEditingId(null);
  };

  useEffect(() => {
    fetchData();
    fetchAgents();
  }, [id]);

  const fetchAgents = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const response = await api.get(`/agents/company/${userData.companyId}`);
        if (response.data.success) {
          setAgents(response.data.agents);
        }
      }
    } catch (error) {
      console.error('Agentlar yüklenirken hata:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch customer details
      const customerResponse = await api.get(`/customers/detail/${id}`);
      if (customerResponse.data.success) {
        const customerData = customerResponse.data.customer;
        setCustomer(customerData);
        // Customer bilgilerini aldıktan sonra formData'yı güncelle
        setFormData(prev => ({
          ...prev,
          customer_id: customerData.id
        }));

        // Customer bilgilerini aldıktan sonra users ve contracts'ları UUID ile çek
        try {
          // Fetch customer users with UUID (backend will handle the conversion)
          const usersResponse = await api.get(`/customer-users/${id}`);
          if (usersResponse.data.success) {
            setUsers(usersResponse.data.users);
          }

          // Fetch customer contracts with UUID (backend will handle the conversion)
          const contractsResponse = await api.get(`/customer-contracts/${id}`);
          if (contractsResponse.data.success) {
            setContracts(contractsResponse.data.contracts);
            
            // Initialize expanded state for each contract
            const initialExpandedState = {};
            const initialFilesVisibleState = {};
            contractsResponse.data.contracts.forEach(contract => {
              initialExpandedState[contract.id] = false;
              initialFilesVisibleState[contract.id] = false;
            });
            setExpandedContracts(initialExpandedState);
            setContractFilesVisible(initialFilesVisibleState);
          }
        } catch (innerError) {
          console.error('Error loading users or contracts:', innerError);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showPopup({
        title: t('common.error'),
        message: t('customerDetail.errorLoadingData'),
        type: 'error',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    showPopup({
      title: t('customerDetail.deleteUser'),
      message: t('customerDetail.confirmDeleteUser', { userName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/customer-users/${userId}`);
          if (response.data.success) {
            setUsers(users.filter(user => user.id !== userId));
            showPopup({
              title: t('common.success'),
              message: t('customerDetail.userDeletedSuccess', { userName }),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('customerDetail.errorDeletingUser'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleEdit = (user) => {
    setFormData({
      user_status: user.user_status === 1,
      full_name: user.full_name,
      username: user.username,
      password: '',
      confirmPassword: '',
      user_email: user.user_email,
      user_gsm: user.user_gsm,
      user_type: user.user_type,
      user_department: user.user_department,
      customer_id: customer ? customer.id : null
    });
    setIsEditing(true);
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Customer ID kontrolü
    if (!formData.customer_id) {
      showPopup({
        title: t('common.error'),
        message: t('customerDetail.customerInfoNotLoaded'),
        type: 'error',
        showCancel: false
      });
      return;
    }
    
    // Password doğrulaması
    if (!isEditing && (!formData.password || !formData.confirmPassword)) {
      showPopup({
        title: t('common.error'),
        message: t('customerDetail.enterPasswordAndConfirm'),
        type: 'error',
        showCancel: false
      });
      return;
    }
    
    if (!isEditing && formData.password !== formData.confirmPassword) {
      showPopup({
        title: t('common.error'),
        message: t('customerDetail.passwordsDoNotMatch'),
        type: 'error',
        showCancel: false
      });
      return;
    }
    
    // Password gereksinimleri kontrolü
    if (!isEditing && formData.password) {
      const password = formData.password;
      if (password.length < 8) {
        showPopup({
          title: t('common.error'),
          message: t('customerDetail.passwordMinLength'),
          type: 'error',
          showCancel: false
        });
        return;
      }
      if (!/[A-Z]/.test(password)) {
        showPopup({
          title: t('common.error'),
          message: t('customerDetail.passwordUppercase'),
          type: 'error',
          showCancel: false
        });
        return;
      }
      if (!/[a-z]/.test(password)) {
        showPopup({
          title: t('common.error'),
          message: t('customerDetail.passwordLowercase'),
          type: 'error',
          showCancel: false
        });
        return;
      }
      if (!/[0-9]/.test(password)) {
        showPopup({
          title: t('common.error'),
          message: t('customerDetail.passwordNumber'),
          type: 'error',
          showCancel: false
        });
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        showPopup({
          title: t('common.error'),
          message: t('customerDetail.passwordSpecialChar'),
          type: 'error',
          showCancel: false
        });
        return;
      }
    }
    
    try {
      let response;
      const submitData = { ...formData };
      delete submitData.confirmPassword; // API'ye gönderilmeden önce confirmPassword'ü kaldır
      
      if (isEditing) {
        response = await api.put(`/customer-users/${editingId}`, submitData);
      } else {
        response = await api.post('/customer-users', submitData);
      }

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: isEditing ? t('customerDetail.userUpdatedSuccess') : t('customerDetail.userAddedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            setShowForm(false);
            setIsEditing(false);
            setEditingId(null);
            fetchData();
            closePopup();
          }
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('customerDetail.errorDuringOperation'),
        type: 'error',
        showCancel: false
      });
    }
  };



  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || '',
      message: options.message || '',
      type: options.type || 'confirm',
      onConfirm: options.onConfirm || null,
      showCancel: options.showCancel !== undefined ? options.showCancel : true
    });
  };

  const closePopup = () => {
    setPopup({
      show: false,
      title: '',
      message: '',
      type: 'confirm',
      onConfirm: null,
      showCancel: true
    });
  };

  // Customer edit and delete functions
  const handleCustomerEdit = () => {
    // Set customer form data for editing
    setCustomerFormData({
      cutomer_status: customer.cutomer_status === 1,
      customer_name: customer.customer_name,
      customer_address: customer.customer_address,
      customer_phone: customer.customer_phone,
      customer_contact: customer.customer_contact,
      customer_contact_email: customer.customer_contact_email,
      customer_contact_phone: customer.customer_contact_phone,
      customer_account_manager: customer.customer_account_manager || '',
      company_id: customer.company_id
    });
    setShowCustomerEditForm(true);
  };

  const handleCustomerChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerFormData.company_id) {
      showPopup({
        title: t('common.error'),
        message: t('customerDetail.companyInfoNotFound'),
        type: 'error',
        showCancel: false
      });
      return;
    }

    try {
      const response = await api.put(`/customers/${customer.id}`, customerFormData);

      if (response.data.success) {
        showPopup({
          title: t('common.success'),
          message: t('customerDetail.customerUpdatedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            setShowCustomerEditForm(false);
            fetchData(); // Refresh customer data
            closePopup();
          }
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('customerDetail.errorDuringOperation'),
        type: 'error',
        showCancel: false,
        onConfirm: closePopup
      });
    }
  };

  const handleCustomerDelete = () => {
    showPopup({
      title: t('customerDetail.deleteCustomer'),
      message: t('customerDetail.confirmDeleteCustomer', { customerName: customer.customer_name }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          // Customer'ın ID'sini kullan (UUID değil)
          const response = await api.delete(`/customers/${customer.id}`);
          if (response.data.success) {
            showPopup({
              title: t('common.success'),
              message: t('customerDetail.customerDeletedSuccess', { customerName: customer.customer_name }),
              type: 'success',
              showCancel: false,
              onConfirm: () => {
                closePopup();
                navigate('/settings/customer');
              }
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('customerDetail.errorDeletingCustomer'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };



  const toggleContractExpand = (contractId) => {
    setExpandedContracts(prev => ({
      ...prev,
      [contractId]: !prev[contractId]
    }));
  };

  const toggleContractFiles = (contractId) => {
    setContractFilesVisible(prev => ({
      ...prev,
      [contractId]: !prev[contractId]
    }));
  };

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  if (!customer) {
    return <div>{t('customerDetail.customerNotFound')}</div>;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm mb-6 pt-4">
        <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
          <Link 
            to="/settings/customer" 
            className="text-[#fff] dark:text-white hover:text-[gray] dark:hover:text-gray-300 font-medium transition-colors duration-200"
          >
            {t('settings.title')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff] dark:text-white" size={20} />
          <Link 
            to="/settings/customer" 
            className="text-[#fff] dark:text-white hover:text-[gray] dark:hover:text-gray-300 font-medium transition-colors duration-200"
          >
            {t('customerSettings.title')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff] dark:text-white" size={20} />
          <span className="text-[#fff] dark:text-white font-medium">
            {customer.customer_name}
          </span>
        </div>
      </nav>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 gap-6 pb-6">
          {/* Customer Details Tablosu */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                         <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
               <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('customerDetail.customerDetails')}</h1>
               <div className="relative">
                 {/* Actions Menu Button */}
                 <button
                   onClick={() => setShowCustomerActionsMenu(!showCustomerActionsMenu)}
                   className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                   title="Actions"
                 >
                   <CiMenuKebab size={20} />
                 </button>
                 
                 {/* Actions Dropdown Menu */}
                 {showCustomerActionsMenu && (
                   <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                     <div className="py-1">
                       <button
                         onClick={() => {
                           handleCustomerEdit();
                           setShowCustomerActionsMenu(false);
                         }}
                         className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                       >
                         <MdEdit size={16} />
                         {t('common.edit')}
                       </button>
                       <button
                         onClick={() => {
                           handleCustomerDelete();
                           setShowCustomerActionsMenu(false);
                         }}
                         className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                       >
                         <MdDelete size={16} />
                         {t('common.delete')}
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             </div>
            <div className="overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    <tr>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerDetail.customerName')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{customer.customer_name}</td>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerDetail.status')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">
                        <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                          customer.cutomer_status ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          {customer.cutomer_status ? t('customerDetail.active') : t('customerDetail.inactive')}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerDetail.contactPerson')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{customer.customer_contact}</td>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerDetail.contactEmail')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{customer.customer_contact_email}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerDetail.contactPhone')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{customer.customer_contact_phone}</td>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerDetail.companyPhone')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{customer.customer_phone}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">Account Manager</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white" colSpan="3">{customer.customer_account_manager || '-'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 rounded-bl-lg">{t('customerDetail.address')}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white " colSpan="3">{customer.customer_address}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4 p-4">
                {/* Customer Name & Status */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.customerName')}</div>
                    <div className="text-gray-900 dark:text-white">{customer.customer_name}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.status')}</div>
                    <div className="text-gray-900 dark:text-white">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        customer.cutomer_status ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                      }`}>
                        {customer.cutomer_status ? t('customerDetail.active') : t('customerDetail.inactive')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Person & Contact Email */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.contactPerson')}</div>
                    <div className="text-gray-900 dark:text-white">{customer.customer_contact}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.contactEmail')}</div>
                    <div className="text-gray-900 dark:text-white">{customer.customer_contact_email}</div>
                  </div>
                </div>

                {/* Contact Phone & Company Phone */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.contactPhone')}</div>
                    <div className="text-gray-900 dark:text-white">{customer.customer_contact_phone}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.companyPhone')}</div>
                    <div className="text-gray-900 dark:text-white">{customer.customer_phone}</div>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Account Manager</div>
                  <div className="text-gray-900 dark:text-white">{customer.customer_account_manager || '-'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.address')}</div>
                  <div className="text-gray-900 dark:text-white">{customer.customer_address}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Users Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('customerDetail.customerUsers')}</h2>
              <div className="relative">
                {/* Actions Menu Button */}
                <button
                  onClick={() => setShowUsersActionsMenu(!showUsersActionsMenu)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                  title="Actions"
                >
                  <CiMenuKebab size={20} />
                </button>
                
                {/* Actions Dropdown Menu */}
                {showUsersActionsMenu && (
                  <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowForm(true);
                          setShowUsersActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdAdd size={16} />
                        {t('customerDetail.addUser')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.status')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.fullName')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.username')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.email')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.phone')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.department')}
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                          {t('customerDetail.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-br-lg alt rounded-bl-lg">
                            {t('customerDetail.noUsersFound')}
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr key={user.id}>
                            <td className="px-4 py-2">
                              <UserStatusBadge isActive={user.user_status === 1} />
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.full_name}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {user.username}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {user.user_email}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {user.user_gsm}
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {user.user_department}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <ActionButtons
                                user={user}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4 p-4">
                {users.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 dark:text-gray-500 text-sm">{t('customerDetail.noUsersFound')}</p>
                  </div>
                ) : (
                  users.map((user) => (
                    <div key={user.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                      {/* Status & Full Name */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.status')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">
                            <UserStatusBadge isActive={user.user_status === 1} />
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.fullName')}</div>
                          <div className="text-sm text-gray-900 dark:text-white font-medium">{user.full_name}</div>
                        </div>
                      </div>

                      {/* Username & Email */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.username')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">{user.username}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.email')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">{user.user_email}</div>
                        </div>
                      </div>

                      {/* Phone & Department */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.phone')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">{user.user_gsm}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.department')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">{user.user_department}</div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.actions')}</div>
                        <div className="flex items-center gap-2">
                          <ActionButtons
                            user={user}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Customer Contracts Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
              <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('customerDetail.customerContracts')}</h2>
            </div>
            <div className="overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.status')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.contractId')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.contractType')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.startDate')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.endDate')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.files')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('customerDetail.details')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {contracts.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg alt">
                            {t('customerDetail.noContractsFound')}
                          </td>
                        </tr>
                      ) : (
                        contracts.map((contract) => (
                          <React.Fragment key={contract.id}>
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-2">
                                <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                                  contract.contract_status ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                                }`}>
                                  {contract.contract_status ? t('customerDetail.active') : t('customerDetail.inactive')}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  <Link 
                                    to={`/settings/contract/${contract.id}`}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                  >
                                    {contract.contract_id}
                                  </Link>
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {contract.contract_type}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {new Date(contract.start_date).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {new Date(contract.end_date).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => toggleContractFiles(contract.id)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium flex items-center gap-1"
                                >
                                  <MdFolder size={16} />
                                  {contractFilesVisible[contract.id] ? t('customerDetail.hideFiles') : t('customerDetail.viewFiles')}
                                </button>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => toggleContractExpand(contract.id)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium"
                                >
                                  {expandedContracts[contract.id] ? t('customerDetail.hideProducts') : t('customerDetail.showProducts')}
                                </button>
                              </td>
                            </tr>
                          {expandedContracts[contract.id] && (
                            <tr>
                              <td colSpan="7" className="px-0 py-0 border-b border-gray-200 dark:border-gray-600">
                                <div className="bg-gray-50 dark:bg-gray-700 px-8 py-4">
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    {t('customerDetail.productsAndSLAs')}
                                  </h4>
                                  <ProductsTable contractId={contract.id} />
                                </div>
                              </td>
                            </tr>
                          )}
                          {contractFilesVisible[contract.id] && (
                            <tr>
                              <td colSpan="7" className="px-0 py-0 border-b border-gray-200 dark:border-gray-600">
                                <div className="bg-gray-50 dark:bg-gray-700 px-8 py-4">
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    {t('customerDetail.contractFiles')}
                                  </h4>
                                  <ContractFilesTable contractId={contract.id} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {contracts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">{t('customerDetail.noContractsFound')}</p>
                </div>
              ) : (
                contracts.map((contract) => (
                  <div key={contract.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                    {/* Status & Contract ID */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.status')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                            contract.contract_status ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                          }`}>
                            {contract.contract_status ? t('customerDetail.active') : t('customerDetail.inactive')}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.contractId')}</div>
                        <div className="text-sm text-gray-900 dark:text-white font-medium">
                          <Link 
                            to={`/settings/contract/${contract.id}`}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          >
                            {contract.contract_id}
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Contract Type & Start Date */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.contractType')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{contract.contract_type}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.startDate')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{new Date(contract.start_date).toLocaleDateString()}</div>
                      </div>
                    </div>

                    {/* End Date & Files */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.endDate')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{new Date(contract.end_date).toLocaleDateString()}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.files')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          <button
                            onClick={() => toggleContractFiles(contract.id)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium flex items-center gap-1"
                          >
                            <MdFolder size={16} />
                            {contractFilesVisible[contract.id] ? t('customerDetail.hideFiles') : t('customerDetail.viewFiles')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerDetail.details')}</div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        <button
                          onClick={() => toggleContractExpand(contract.id)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium"
                        >
                          {expandedContracts[contract.id] ? t('customerDetail.hideProducts') : t('customerDetail.showProducts')}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedContracts[contract.id] && (
                      <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-4 mt-3">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('customerDetail.productsAndSLAs')}
                        </h4>
                        <ProductsTable contractId={contract.id} />
                      </div>
                    )}

                    {contractFilesVisible[contract.id] && (
                      <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-4 mt-3">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('customerDetail.contractFiles')}
                        </h4>
                        <ContractFilesTable contractId={contract.id} />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

             {/* User Form Modal */}
       {showForm && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
           <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
             {/* Header */}
             <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
               <h3 className="text-lg font-medium">
                 {isEditing ? t('customerDetail.editUser') : t('customerDetail.addNewUser')}
               </h3>
               <button 
                 onClick={() => setShowForm(false)} 
                 className="text-white hover:text-gray-200"
               >
                 <MdClose size={24} />
               </button>
             </div>
             
             <div className="p-8 bg-white dark:bg-gray-800">
               <form onSubmit={handleSubmit} className="space-y-4">
                 {/* Status */}
                 <div className="flex items-center">
                   <input
                     type="checkbox"
                     id="user_status"
                     name="user_status"
                     checked={formData.user_status}
                     onChange={(e) => setFormData({ ...formData, user_status: e.target.checked })}
                     className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600"
                   />
                   <label htmlFor="user_status" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                     {t('customerDetail.active')}
                   </label>
                 </div>

                 {/* Full Name */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.fullName')}
                   </label>
                   <input
                     type="text"
                     name="full_name"
                     value={formData.full_name}
                     onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>

                 {/* Username */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.username')}
                   </label>
                   <input
                     type="text"
                     name="username"
                     value={formData.username}
                     onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                     autoComplete="off"
                   />
                 </div>

                 {/* Password */}
                 <div className="relative">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {isEditing ? t('customerDetail.newPassword') : t('customerDetail.password')}
                   </label>
                   <input
                     type="password"
                     name="password"
                     value={formData.password}
                     onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                     onFocus={() => setShowPasswordTooltip(true)}
                     onBlur={() => setShowPasswordTooltip(false)}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required={!isEditing}
                     autoComplete="new-password"
                     data-lpignore="true"
                     data-form-type="other"
                   />
                   {showPasswordTooltip && (
                     <div className="absolute z-10 mt-1 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg border border-gray-600 min-w-[280px]">
                       <div className="font-medium mb-2">{t('customerDetail.passwordRequirements')}</div>
                       <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                         <div className={`${formData.password.length >= 8 ? 'text-green-400' : 'text-red-400'}`}>
                           • {t('customerDetail.minCharacters')} {formData.password.length >= 8 ? '✓' : '✗'}
                         </div>
                         <div className={`${/[A-Z]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                           • {t('customerDetail.uppercase')} {/[A-Z]/.test(formData.password) ? '✓' : '✗'}
                         </div>
                         <div className={`${/[a-z]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                           • {t('customerDetail.lowercase')} {/[a-z]/.test(formData.password) ? '✓' : '✗'}
                         </div>
                         <div className={`${/[0-9]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                           • {t('customerDetail.number')} {/[0-9]/.test(formData.password) ? '✓' : '✗'}
                         </div>
                         <div className={`col-span-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                           • {t('customerDetail.specialChar')} {/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? '✓' : '✗'}
                         </div>
                       </div>
                       <div className="absolute top-0 left-4 transform -translate-y-full">
                         <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                       </div>
                     </div>
                   )}
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.confirmPassword')}
                   </label>
                   <input
                     type="password"
                     value={formData.confirmPassword}
                     onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                     className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                       formData.confirmPassword && formData.password !== formData.confirmPassword
                         ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                         : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                     } text-gray-900 dark:text-white`}
                     autoComplete="new-password"
                     data-lpignore="true"
                     data-form-type="other"
                     required={!isEditing}
                   />
                   {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                     <div className="mt-1 text-xs text-red-600">
                       {t('customerDetail.passwordsDoNotMatch')}
                     </div>
                   )}
                 </div>

                 {/* Email */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.email')}
                   </label>
                   <input
                     type="email"
                     name="user_email"
                     value={formData.user_email}
                     onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>

                 {/* Phone */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.phone')}
                   </label>
                   <InputMask
                     mask="(999) 999-9999"
                     type="tel"
                     name="user_gsm"
                     value={formData.user_gsm}
                     onChange={(e) => setFormData({ ...formData, user_gsm: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                     placeholder="(555) 555-5555"
                   />
                 </div>

                 {/* Department */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.department')}
                   </label>
                   <input
                     type="text"
                     name="user_department"
                     value={formData.user_department}
                     onChange={(e) => setFormData({ ...formData, user_department: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>

                 {/* Form Buttons */}
                 <div className="flex justify-end gap-3 mt-6">
                   <button
                     type="button"
                     onClick={() => {
                       resetFormData();
                       setShowForm(false);
                     }}
                     className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                   >
                     {t('common.cancel')}
                   </button>
                   <button
                     type="submit"
                     className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                   >
                     {isEditing ? t('customerDetail.updateUser') : t('customerDetail.addUser')}
                   </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}

       {/* Customer Edit Form Modal */}
       {showCustomerEditForm && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
           <div className="relative mx-auto w-[800px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
             {/* Header */}
             <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
               <h3 className="text-lg font-medium">
                 {t('customerDetail.editCustomer')}
               </h3>
               <button 
                 onClick={() => setShowCustomerEditForm(false)} 
                 className="text-white hover:text-gray-200"
               >
                 <MdClose size={24} />
               </button>
             </div>
             
             <div className="p-8 bg-white dark:bg-gray-800">
               <form onSubmit={handleCustomerSubmit} className="grid grid-cols-2 gap-4">
                 {/* Status */}
                 <div className="col-span-2 flex items-center">
                   <input
                     type="checkbox"
                     id="cutomer_status"
                     name="cutomer_status"
                     checked={customerFormData.cutomer_status}
                     onChange={handleCustomerChange}
                     className="w-4 h-4 text-blue-600 rounded border-gray-300"
                   />
                   <label htmlFor="cutomer_status" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                     {t('customerDetail.active')}
                   </label>
                 </div>

                 {/* Customer Name */}
                 <div className="col-span-2">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.customerName')}
                   </label>
                   <input
                     type="text"
                     name="customer_name"
                     value={customerFormData.customer_name}
                     onChange={handleCustomerChange}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>

                 {/* Contact Person */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.contactPerson')}
                   </label>
                   <input
                     type="text"
                     name="customer_contact"
                     value={customerFormData.customer_contact}
                     onChange={handleCustomerChange}
                     autoComplete="new-contact"
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>

                 {/* Contact Email */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.contactEmail')}
                   </label>
                   <input
                     type="email"
                     name="customer_contact_email"
                     value={customerFormData.customer_contact_email}
                     onChange={handleCustomerChange}
                     autoComplete="new-email"
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>

                 {/* Contact Phone */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.contactPhone')}
                   </label>
                   <InputMask
                     mask="(999) 999-9999"
                     type="tel"
                     name="customer_contact_phone"
                     value={customerFormData.customer_contact_phone}
                     onChange={handleCustomerChange}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     placeholder="(555) 555-5555"
                   />
                 </div>

                 {/* Company Phone */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.companyPhone')}
                   </label>
                   <InputMask
                     mask="(999) 999-9999"
                     type="tel"
                     name="customer_phone"
                     value={customerFormData.customer_phone}
                     onChange={handleCustomerChange}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     placeholder="(555) 555-5555"
                   />
                 </div>

                 {/* Account Manager */}
                 <div className="col-span-2">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     Account Manager
                   </label>
                   <select
                     name="customer_account_manager"
                     value={customerFormData.customer_account_manager}
                     onChange={handleCustomerChange}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="">Account Manager Seçin</option>
                     {agents.map(agent => (
                       <option key={agent.id} value={agent.full_name}>
                         {agent.full_name}
                       </option>
                     ))}
                   </select>
                 </div>

                 {/* Address */}
                 <div className="col-span-2">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('customerDetail.address')}
                   </label>
                   <textarea
                     name="customer_address"
                     value={customerFormData.customer_address}
                     onChange={handleCustomerChange}
                     rows={3}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   />
                 </div>

                 {/* Form Buttons */}
                 <div className="col-span-2 flex justify-end gap-3 mt-4">
                   <button
                     type="button"
                     onClick={() => setShowCustomerEditForm(false)}
                     className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                   >
                     {t('common.cancel')}
                   </button>
                   <button
                     type="submit"
                     className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                   >
                     {t('customerDetail.updateCustomer')}
                   </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}

             {/* Popup Component */}
       {popup.show && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
           <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
             {/* Header */}
             <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
               <h3 className="text-lg font-medium">{popup.title}</h3>
               <button
                 onClick={closePopup}
                 className="text-white hover:text-gray-200"
               >
                 <MdClose size={24} />
               </button>
             </div>
             
             <div className="p-8 bg-white dark:bg-gray-800">
               <div className="space-y-6">
                 <div className="text-center">
                   <p className="text-base text-gray-600 dark:text-gray-300">
                     {popup.message}
                   </p>
                 </div>

                 <div className="flex justify-center gap-3">
                   {popup.showCancel && (
                     <button
                       onClick={closePopup}
                       className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                     >
                       {t('common.cancel')}
                     </button>
                   )}
                   <button
                     onClick={() => {
                       if (popup.onConfirm) {
                         popup.onConfirm();
                       } else {
                         closePopup();
                       }
                     }}
                     className={`px-4 py-2 text-white rounded-md ${
                       popup.type === 'error' 
                         ? 'bg-red-600 hover:bg-red-700'
                         : popup.type === 'success'
                         ? 'bg-green-600 hover:bg-green-700'
                         : 'bg-[#272c39] hover:bg-[#1e2230]'
                     }`}
                   >
                     {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
      </div>
    </div>
  );
};


export default CustomerDetail; 